import React, { useState, useEffect, useMemo } from 'react';
import { 
  Plus, 
  Edit2, 
  ChevronDown, 
  ChevronUp, 
  Search, 
  Filter, 
  ArrowUpDown, 
  FileText, 
  DollarSign, 
  X, 
  RotateCcw,
  Calendar,
  CalendarRange,
  SlidersHorizontal,
  UserCheck,
  CheckCircle2,
  Clock,
  Send,
  XCircle,
  Hash,
  ShieldAlert,
  AlertTriangle,
  AlertCircle
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { PropostaForm } from '../pages/PropostaForm';
import { PropostaDetailAccordion } from '../components/proposta/PropostaDetailAccordion';
import { Pagination } from '../components/common/Pagination';
import { generatePropostaPdf } from '../services/propostaPdfGenerator';
import { fixEncodingIssues } from '../utils/textUtils';
import { propostaMetaService } from '../services/propostaMetaService';
import { propostaSaldoService, PropostaSaldoInfo } from '../services/propostaSaldoService';
import { ProtectedField } from '../components/common/ProtectedField';
import { getClientesApenasList } from '../services/pessoaService';

export function Propostas() {
  const [propostas, setPropostas] = useState<any[]>([]);
  const [saldosPorProposta, setSaldosPorProposta] = useState<Map<number, PropostaSaldoInfo>>(new Map());
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedPropostaId, setSelectedPropostaId] = useState<number | null>(null);
  const [expandedIds, setExpandedIds] = useState<number[]>([]);
  const [generatingPdfId, setGeneratingPdfId] = useState<number | null>(null);

  // Search & Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('Todos');
  const [clienteFilter, setClienteFilter] = useState('Todos');
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [minVal, setMinVal] = useState('');
  const [maxVal, setMaxVal] = useState('');
  const [sortBy, setSortBy] = useState('recentes'); // 'recentes', 'antigos', 'valor_cresc', 'valor_decresc', 'cliente', 'numero'
  const [mostrarFiltrosAvancados, setMostrarFiltrosAvancados] = useState(false);
  const [mostrarFiltros, setMostrarFiltros] = useState(false);

  // Clientes para filtro
  const [clientesList, setClientesList] = useState<{ id: number; nome: string }[]>([]);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);

  useEffect(() => {
    fetchPropostas();
    getClientesApenasList().then(list => setClientesList(list || []));

    // Inscreve-se nas mudanças em tempo real da tabela 'pedido'
    const channel = supabase
      .channel('pedido_changes')
      .on(
        'postgres_changes',
        {
          event: '*', // Escuta INSERT, UPDATE e DELETE
          schema: 'public',
          table: 'pedido'
        },
        () => {
          fetchPropostas();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const fetchPropostas = async () => {
    const { data } = await supabase
      .from('pedido')
      .select('*, pessoa(id, nome, documento), detalhes_do_pedido(id, servicos, valor_total, quantidade, valor_unitario, tipo_servico_id)');
    const enriched = (data || []).map((p: any) => propostaMetaService.enrichProposta(p));
    setPropostas(enriched);

    try {
      const saldos = await propostaSaldoService.getBatchSaldos(enriched);
      setSaldosPorProposta(saldos);
    } catch (e) {
      console.warn('Erro ao carregar saldos das propostas:', e);
    }
  };

  // Reset page to 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, clienteFilter, dataInicio, dataFim, minVal, maxVal, sortBy]);

  const toggleExpand = (id: number) => {
    setExpandedIds(prev =>
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  const calculateTotal = (p: any) => {
    if (p.total_geral && Number(p.total_geral) > 0) {
      return Number(p.total_geral);
    }
    if (p.detalhes_do_pedido && Array.isArray(p.detalhes_do_pedido)) {
      return p.detalhes_do_pedido.reduce((acc: number, item: any) => {
        const itemTotal = Number(item.valor_total) || (Number(item.quantidade || 0) * Number(item.valor_unitario || 0));
        return acc + itemTotal;
      }, 0);
    }
    return 0;
  };

  const handleEdit = (id: number) => {
    setSelectedPropostaId(id);
    setIsFormOpen(true);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setSelectedPropostaId(null);
    fetchPropostas();
  };

  const handleDownloadPdf = async (proposta: any) => {
    try {
      setGeneratingPdfId(proposta.id);
      await generatePropostaPdf(proposta);
    } catch (error) {
      console.error('Erro ao gerar PDF da proposta:', error);
    } finally {
      setGeneratingPdfId(null);
    }
  };

  // Status counts
  const statusCounts = useMemo(() => {
    return {
      todos: propostas.length,
      aprovado: propostas.filter(p => (p.status || '').toLowerCase() === 'aprovado').length,
      rascunho: propostas.filter(p => !p.status || (p.status || '').toLowerCase() === 'rascunho').length,
      enviado: propostas.filter(p => (p.status || '').toLowerCase() === 'enviado').length,
      reprovado: propostas.filter(p => (p.status || '').toLowerCase() === 'reprovado').length,
    };
  }, [propostas]);

  // Date Presets
  const handleDatePreset = (preset: 'hoje' | 'mes' | '30dias' | 'ano' | 'limpar') => {
    const now = new Date();
    if (preset === 'limpar') {
      setDataInicio('');
      setDataFim('');
      return;
    }
    if (preset === 'hoje') {
      const iso = now.toISOString().split('T')[0];
      setDataInicio(iso);
      setDataFim(iso);
      return;
    }
    if (preset === 'mes') {
      const start = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
      const end = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
      setDataInicio(start);
      setDataFim(end);
      return;
    }
    if (preset === '30dias') {
      const past = new Date();
      past.setDate(past.getDate() - 30);
      setDataInicio(past.toISOString().split('T')[0]);
      setDataFim(now.toISOString().split('T')[0]);
      return;
    }
    if (preset === 'ano') {
      const start = `${now.getFullYear()}-01-01`;
      const end = `${now.getFullYear()}-12-31`;
      setDataInicio(start);
      setDataFim(end);
      return;
    }
  };

  const handleClearAllFilters = () => {
    setSearchTerm('');
    setStatusFilter('Todos');
    setClienteFilter('Todos');
    setDataInicio('');
    setDataFim('');
    setMinVal('');
    setMaxVal('');
    setSortBy('recentes');
  };

  const formatDisplayDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    try {
      const raw = dateStr.split('T')[0];
      const parts = raw.split('-');
      if (parts.length === 3) {
        return `${parts[2]}/${parts[1]}/${parts[0]}`;
      }
      return dateStr;
    } catch {
      return dateStr;
    }
  };

  // Filter propostas
  const filteredPropostas = useMemo(() => {
    return propostas.filter(p => {
      const term = searchTerm.toLowerCase().trim();
      const clientName = (p.pessoa?.nome || '').toLowerCase();
      const clientDoc = (p.pessoa?.documento || '').toLowerCase();
      const numeroProp = String(p.numero_proposta || p.id || '').toLowerCase();
      const services = (p.detalhes_do_pedido || [])
        .map((d: any) => (d.servicos || '').toLowerCase())
        .join(' ');

      const matchesSearch =
        !term ||
        clientName.includes(term) ||
        clientDoc.includes(term) ||
        numeroProp.includes(term) ||
        `#${numeroProp}`.includes(term) ||
        services.includes(term);

      // Status
      const pStatus = p.status || 'Rascunho';
      const matchesStatus =
        statusFilter === 'Todos' ||
        pStatus.toLowerCase() === statusFilter.toLowerCase();

      // Cliente
      const matchesCliente =
        clienteFilter === 'Todos' ||
        String(p.pessoa_id || p.pessoa?.id) === String(clienteFilter);

      // Datas (data_abertura ou created_at)
      const pDate = p.data_abertura || (p.created_at ? p.created_at.split('T')[0] : '');
      const matchesDataInicio = !dataInicio || (pDate && pDate >= dataInicio);
      const matchesDataFim = !dataFim || (pDate && pDate <= dataFim);

      // Faixa de Valores
      const total = calculateTotal(p);
      const matchesMin = minVal === '' || total >= parseFloat(minVal);
      const matchesMax = maxVal === '' || total <= parseFloat(maxVal);

      return (
        matchesSearch &&
        matchesStatus &&
        matchesCliente &&
        matchesDataInicio &&
        matchesDataFim &&
        matchesMin &&
        matchesMax
      );
    });
  }, [propostas, searchTerm, statusFilter, clienteFilter, dataInicio, dataFim, minVal, maxVal]);

  // Sort propostas
  const sortedPropostas = useMemo(() => {
    return [...filteredPropostas].sort((a, b) => {
      if (sortBy === 'recentes') {
        const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
        const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
        return dateB - dateA;
      }
      if (sortBy === 'antigos') {
        const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
        const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
        return dateA - dateB;
      }
      if (sortBy === 'valor_cresc') {
        return calculateTotal(a) - calculateTotal(b);
      }
      if (sortBy === 'valor_decresc') {
        return calculateTotal(b) - calculateTotal(a);
      }
      if (sortBy === 'cliente') {
        const nameA = a.pessoa?.nome || '';
        const nameB = b.pessoa?.nome || '';
        return nameA.localeCompare(nameB);
      }
      if (sortBy === 'numero') {
        return Number(b.id) - Number(a.id);
      }
      return 0;
    });
  }, [filteredPropostas, sortBy]);

  // Total valor filtrado
  const totalValorFiltrado = useMemo(() => {
    return filteredPropostas.reduce((acc, p) => acc + calculateTotal(p), 0);
  }, [filteredPropostas]);

  // Active filters count
  const activeFiltersCount = [
    searchTerm ? 1 : 0,
    statusFilter !== 'Todos' ? 1 : 0,
    clienteFilter !== 'Todos' ? 1 : 0,
    dataInicio ? 1 : 0,
    dataFim ? 1 : 0,
    minVal !== '' ? 1 : 0,
    maxVal !== '' ? 1 : 0,
    sortBy !== 'recentes' ? 1 : 0,
  ].reduce((a, b) => a + b, 0);

  const advancedFiltersActiveCount = [
    dataInicio ? 1 : 0,
    dataFim ? 1 : 0,
    minVal !== '' ? 1 : 0,
    maxVal !== '' ? 1 : 0,
  ].reduce((a, b) => a + b, 0);

  // Paginated list
  const paginatedPropostas = sortedPropostas.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <div className="p-3 sm:p-6 max-w-full">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5 sm:mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-800">Propostas / Contratos</h1>
          <p className="text-xs text-slate-500 mt-0.5">
            Gerenciamento de propostas comerciais, cotas de viagens e saldo operacional
          </p>
        </div>
        <ProtectedField permission="manage_proposals" hideMode="remove">
          <button 
            type="button"
            onClick={() => setIsFormOpen(true)} 
            className="w-full sm:w-auto flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 transition-colors text-white px-4 py-2.5 rounded-xl font-semibold text-xs sm:text-sm shadow-sm cursor-pointer"
          >
            <Plus size={18} /> Nova Proposta
          </button>
        </ProtectedField>
      </div>

      {/* Barra Compacta de Busca e Ação de Filtros (Filtros ocultados por padrão) */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 mb-4">
        <div className="flex-1 relative">
          <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
            <Search size={16} />
          </span>
          <input
            type="text"
            placeholder="Buscar por nº da proposta, cliente, CNPJ/CPF ou serviço..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-9 py-2.5 bg-white border border-slate-200 rounded-xl text-xs placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-red-500 transition-all text-slate-800 font-medium shadow-xs"
          />
          {searchTerm && (
            <button
              type="button"
              onClick={() => setSearchTerm('')}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600 cursor-pointer"
              title="Limpar busca"
            >
              <X size={14} />
            </button>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={() => setMostrarFiltros(!mostrarFiltros)}
            className={`px-4 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center gap-2 cursor-pointer border shadow-xs ${
              mostrarFiltros || activeFiltersCount > 0
                ? 'bg-slate-900 text-white border-slate-900'
                : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
            }`}
            title={mostrarFiltros ? 'Ocultar filtros' : 'Abrir filtros avançados'}
          >
            <Filter size={14} className={mostrarFiltros || activeFiltersCount > 0 ? 'text-red-400' : 'text-slate-500'} />
            <span>{mostrarFiltros ? 'Ocultar Filtros' : 'Filtros'}</span>
            {activeFiltersCount > 0 && (
              <span className="px-1.5 py-0.2 bg-red-600 text-white rounded-full text-[10px] font-black">
                {activeFiltersCount}
              </span>
            )}
            {mostrarFiltros ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>

          {activeFiltersCount > 0 && (
            <button
              type="button"
              onClick={handleClearAllFilters}
              className="px-3 py-2.5 bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 rounded-xl text-xs font-bold transition-colors flex items-center gap-1.5 cursor-pointer shadow-2xs"
              title="Limpar todos os filtros ativos"
            >
              <RotateCcw size={13} />
              <span className="hidden sm:inline">Limpar</span>
            </button>
          )}
        </div>
      </div>

      {/* Chips Rápidos quando os Filtros estiverem ocultados */}
      {!mostrarFiltros && activeFiltersCount > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 mb-4 bg-white p-3 rounded-xl border border-slate-200 text-xs">
          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mr-1 flex items-center gap-1">
            <Filter size={11} /> Filtros Ativos ({filteredPropostas.length} encontradas):
          </span>
          {statusFilter !== 'Todos' && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-bold bg-blue-50 text-blue-700">
              Status: {statusFilter}
              <button type="button" onClick={() => setStatusFilter('Todos')} className="hover:text-red-600">
                <X size={11} />
              </button>
            </span>
          )}
          {clienteFilter !== 'Todos' && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-bold bg-purple-50 text-purple-700">
              Cliente: {clientesList.find(c => String(c.id) === clienteFilter)?.nome || clienteFilter}
              <button type="button" onClick={() => setClienteFilter('Todos')} className="hover:text-red-600">
                <X size={11} />
              </button>
            </span>
          )}
          {(dataInicio || dataFim) && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-bold bg-amber-50 text-amber-700">
              Data: {dataInicio ? formatDisplayDate(dataInicio) : 'início'} até {dataFim ? formatDisplayDate(dataFim) : 'hoje'}
              <button type="button" onClick={() => { setDataInicio(''); setDataFim(''); }} className="hover:text-red-600">
                <X size={11} />
              </button>
            </span>
          )}
          {(minVal || maxVal) && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-bold bg-emerald-50 text-emerald-700">
              Faixa: {minVal ? `≥ R$ ${minVal}` : ''} {maxVal ? `≤ R$ ${maxVal}` : ''}
              <button type="button" onClick={() => { setMinVal(''); setMaxVal(''); }} className="hover:text-red-600">
                <X size={11} />
              </button>
            </span>
          )}
          {sortBy !== 'recentes' && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-bold bg-slate-100 text-slate-700">
              Ordem: {sortBy}
              <button type="button" onClick={() => setSortBy('recentes')} className="hover:text-red-600">
                <X size={11} />
              </button>
            </span>
          )}
          <button
            type="button"
            onClick={() => setMostrarFiltros(true)}
            className="text-[11px] font-bold text-red-600 hover:underline ml-auto"
          >
            Editar filtros
          </button>
        </div>
      )}

      {/* Painel de Filtros Detalhados (Exibido sob demanda) */}
      {mostrarFiltros && (
      <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs mb-6 space-y-4 animate-in fade-in duration-150">
        
        {/* Abas / Pílulas Rápidas de Status */}
        <div className="flex items-center gap-2 overflow-x-auto pb-1 border-b border-slate-100">
          <button
            type="button"
            onClick={() => setStatusFilter('Todos')}
            className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer whitespace-nowrap ${
              statusFilter === 'Todos'
                ? 'bg-slate-900 text-white shadow-xs'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            <span>Todos</span>
            <span className={`px-1.5 py-0.2 rounded-full text-[10px] font-black ${
              statusFilter === 'Todos' ? 'bg-slate-700 text-white' : 'bg-slate-200 text-slate-700'
            }`}>
              {statusCounts.todos}
            </span>
          </button>

          <button
            type="button"
            onClick={() => setStatusFilter('Aprovado')}
            className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer whitespace-nowrap ${
              statusFilter.toLowerCase() === 'aprovado'
                ? 'bg-emerald-600 text-white shadow-xs'
                : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
            }`}
          >
            <CheckCircle2 size={13} />
            <span>Aprovados</span>
            <span className={`px-1.5 py-0.2 rounded-full text-[10px] font-black ${
              statusFilter.toLowerCase() === 'aprovado' ? 'bg-emerald-800 text-white' : 'bg-emerald-200 text-emerald-800'
            }`}>
              {statusCounts.aprovado}
            </span>
          </button>

          <button
            type="button"
            onClick={() => setStatusFilter('Rascunho')}
            className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer whitespace-nowrap ${
              statusFilter.toLowerCase() === 'rascunho'
                ? 'bg-amber-500 text-white shadow-xs'
                : 'bg-amber-50 text-amber-700 hover:bg-amber-100'
            }`}
          >
            <Clock size={13} />
            <span>Rascunhos</span>
            <span className={`px-1.5 py-0.2 rounded-full text-[10px] font-black ${
              statusFilter.toLowerCase() === 'rascunho' ? 'bg-amber-700 text-white' : 'bg-amber-200 text-amber-800'
            }`}>
              {statusCounts.rascunho}
            </span>
          </button>

          <button
            type="button"
            onClick={() => setStatusFilter('Enviado')}
            className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer whitespace-nowrap ${
              statusFilter.toLowerCase() === 'enviado'
                ? 'bg-blue-600 text-white shadow-xs'
                : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
            }`}
          >
            <Send size={13} />
            <span>Enviados</span>
            <span className={`px-1.5 py-0.2 rounded-full text-[10px] font-black ${
              statusFilter.toLowerCase() === 'enviado' ? 'bg-blue-800 text-white' : 'bg-blue-200 text-blue-800'
            }`}>
              {statusCounts.enviado}
            </span>
          </button>

          <button
            type="button"
            onClick={() => setStatusFilter('Reprovado')}
            className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer whitespace-nowrap ${
              statusFilter.toLowerCase() === 'reprovado'
                ? 'bg-rose-600 text-white shadow-xs'
                : 'bg-rose-50 text-rose-700 hover:bg-rose-100'
            }`}
          >
            <XCircle size={13} />
            <span>Reprovados</span>
            <span className={`px-1.5 py-0.2 rounded-full text-[10px] font-black ${
              statusFilter.toLowerCase() === 'reprovado' ? 'bg-rose-800 text-white' : 'bg-rose-200 text-rose-800'
            }`}>
              {statusCounts.reprovado}
            </span>
          </button>
        </div>

        {/* Linha Principal de Controles de Busca e Filtros */}
        <div className="flex flex-col lg:flex-row gap-3 items-stretch lg:items-center justify-between">
          
          {/* Campo de Busca Texto */}
          <div className="flex-1 relative min-w-[280px]">
            <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
              <Search size={16} />
            </span>
            <input
              type="text"
              placeholder="Buscar por nº da proposta, cliente, CNPJ/CPF ou serviço..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-9 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:bg-white transition-all text-slate-800 font-medium"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm('')}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600 cursor-pointer"
                title="Limpar busca"
              >
                <X size={14} />
              </button>
            )}
          </div>

          {/* Filtros em Linha: Cliente, Ordenação e Botão Avançado */}
          <div className="flex flex-wrap items-center gap-2.5">
            
            {/* Filtro por Cliente Específico */}
            <div className="min-w-[180px]">
              <div className="relative">
                <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                  <UserCheck size={14} />
                </span>
                <select
                  value={clienteFilter}
                  onChange={(e) => setClienteFilter(e.target.value)}
                  className="w-full pl-8 pr-8 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-700 font-semibold focus:outline-none focus:ring-2 focus:ring-red-500 focus:bg-white transition-all appearance-none cursor-pointer"
                >
                  <option value="Todos">Todos os Clientes</option>
                  {clientesList.map(c => (
                    <option key={c.id} value={String(c.id)}>
                      {c.nome}
                    </option>
                  ))}
                </select>
                <span className="absolute inset-y-0 right-0 pr-2.5 flex items-center pointer-events-none text-slate-400">
                  <ChevronDown size={14} />
                </span>
              </div>
            </div>

            {/* Ordenação */}
            <div className="min-w-[160px]">
              <div className="relative">
                <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                  <ArrowUpDown size={14} />
                </span>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full pl-8 pr-8 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-700 font-semibold focus:outline-none focus:ring-2 focus:ring-red-500 focus:bg-white transition-all appearance-none cursor-pointer"
                >
                  <option value="recentes">Mais Recentes</option>
                  <option value="antigos">Mais Antigos</option>
                  <option value="valor_decresc">Maior Valor (R$)</option>
                  <option value="valor_cresc">Menor Valor (R$)</option>
                  <option value="cliente">Cliente (A-Z)</option>
                  <option value="numero">Nº da Proposta</option>
                </select>
                <span className="absolute inset-y-0 right-0 pr-2.5 flex items-center pointer-events-none text-slate-400">
                  <ChevronDown size={14} />
                </span>
              </div>
            </div>

            {/* Botão de Filtros Avançados (Data e Faixa de Valor) */}
            <button
              type="button"
              onClick={() => setMostrarFiltrosAvancados(!mostrarFiltrosAvancados)}
              className={`px-3 py-2.5 rounded-xl text-xs font-bold transition-colors flex items-center gap-1.5 cursor-pointer border ${
                mostrarFiltrosAvancados || advancedFiltersActiveCount > 0
                  ? 'bg-red-50 text-red-700 border-red-200'
                  : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'
              }`}
              title="Filtrar por Período de Datas e Faixa de Valor"
            >
              <SlidersHorizontal size={14} />
              <span>Filtros Avançados</span>
              {advancedFiltersActiveCount > 0 && (
                <span className="px-1.5 py-0.2 bg-red-600 text-white rounded-full text-[10px] font-black">
                  {advancedFiltersActiveCount}
                </span>
              )}
            </button>

            {/* Botão Limpar Tudo */}
            {activeFiltersCount > 0 && (
              <button
                type="button"
                onClick={handleClearAllFilters}
                className="px-3 py-2.5 bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 rounded-xl text-xs font-bold transition-colors flex items-center gap-1.5 cursor-pointer shadow-2xs"
                title="Limpar todos os filtros ativos"
              >
                <RotateCcw size={13} />
                <span>Limpar</span>
                <span className="ml-0.5 px-1.5 py-0.2 bg-rose-600 text-white rounded-full text-[10px] font-black">
                  {activeFiltersCount}
                </span>
              </button>
            )}

          </div>
        </div>

        {/* Painel Retrátil de Filtros Avançados: Datas e Faixa de Preço */}
        {mostrarFiltrosAvancados && (
          <div className="pt-3 border-t border-slate-100 grid grid-cols-1 md:grid-cols-2 gap-4 bg-slate-50/70 p-4 rounded-xl">
            {/* Filtro por Período / Datas */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold text-slate-600 uppercase tracking-wider flex items-center gap-1.5">
                  <CalendarRange size={13} className="text-red-500" />
                  Período da Proposta
                </span>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => handleDatePreset('hoje')}
                    className="text-[10px] font-bold px-2 py-0.5 bg-white border border-slate-200 rounded-md text-slate-600 hover:bg-slate-100"
                  >
                    Hoje
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDatePreset('mes')}
                    className="text-[10px] font-bold px-2 py-0.5 bg-white border border-slate-200 rounded-md text-slate-600 hover:bg-slate-100"
                  >
                    Este Mês
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDatePreset('30dias')}
                    className="text-[10px] font-bold px-2 py-0.5 bg-white border border-slate-200 rounded-md text-slate-600 hover:bg-slate-100"
                  >
                    30 Dias
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDatePreset('ano')}
                    className="text-[10px] font-bold px-2 py-0.5 bg-white border border-slate-200 rounded-md text-slate-600 hover:bg-slate-100"
                  >
                    Este Ano
                  </button>
                  {(dataInicio || dataFim) && (
                    <button
                      type="button"
                      onClick={() => handleDatePreset('limpar')}
                      className="text-[10px] font-bold px-2 py-0.5 bg-rose-50 border border-rose-200 rounded-md text-rose-600 hover:bg-rose-100"
                    >
                      Limpar
                    </button>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] font-bold text-slate-400 block mb-0.5">A partir de</label>
                  <input
                    type="date"
                    value={dataInicio}
                    onChange={(e) => setDataInicio(e.target.value)}
                    className="w-full px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg text-xs text-slate-700 font-medium focus:ring-2 focus:ring-red-500 outline-none"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-bold text-slate-400 block mb-0.5">Até</label>
                  <input
                    type="date"
                    value={dataFim}
                    onChange={(e) => setDataFim(e.target.value)}
                    className="w-full px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg text-xs text-slate-700 font-medium focus:ring-2 focus:ring-red-500 outline-none"
                  />
                </div>
              </div>
            </div>

            {/* Filtro por Faixa de Valor */}
            <div className="space-y-2">
              <span className="text-[11px] font-bold text-slate-600 uppercase tracking-wider flex items-center gap-1.5">
                <DollarSign size={13} className="text-emerald-600" />
                Faixa de Valor Contratado (R$)
              </span>
              <div className="grid grid-cols-2 gap-2">
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none text-xs font-bold text-slate-400">
                    R$
                  </span>
                  <input
                    type="number"
                    placeholder="Valor Mínimo"
                    value={minVal}
                    onChange={(e) => setMinVal(e.target.value)}
                    className="w-full pl-8 pr-6 py-1.5 bg-white border border-slate-200 rounded-lg text-xs text-slate-800 font-medium focus:ring-2 focus:ring-red-500 outline-none"
                  />
                  {minVal && (
                    <button
                      type="button"
                      onClick={() => setMinVal('')}
                      className="absolute inset-y-0 right-0 pr-2 flex items-center text-slate-400 hover:text-slate-600"
                    >
                      <X size={12} />
                    </button>
                  )}
                </div>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none text-xs font-bold text-slate-400">
                    R$
                  </span>
                  <input
                    type="number"
                    placeholder="Valor Máximo"
                    value={maxVal}
                    onChange={(e) => setMaxVal(e.target.value)}
                    className="w-full pl-8 pr-6 py-1.5 bg-white border border-slate-200 rounded-lg text-xs text-slate-800 font-medium focus:ring-2 focus:ring-red-500 outline-none"
                  />
                  {maxVal && (
                    <button
                      type="button"
                      onClick={() => setMaxVal('')}
                      className="absolute inset-y-0 right-0 pr-2 flex items-center text-slate-400 hover:text-slate-600"
                    >
                      <X size={12} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Barra de Etiquetas de Filtros Ativos */}
        {activeFiltersCount > 0 && (
          <div className="flex flex-wrap items-center gap-1.5 pt-2 border-t border-slate-100">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mr-1 flex items-center gap-1">
              <Filter size={11} /> Filtros Ativos:
            </span>

            {searchTerm && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-bold bg-slate-100 text-slate-700">
                Busca: "{searchTerm}"
                <button type="button" onClick={() => setSearchTerm('')} className="hover:text-red-600">
                  <X size={11} />
                </button>
              </span>
            )}

            {statusFilter !== 'Todos' && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-bold bg-blue-50 text-blue-700">
                Status: {statusFilter}
                <button type="button" onClick={() => setStatusFilter('Todos')} className="hover:text-red-600">
                  <X size={11} />
                </button>
              </span>
            )}

            {clienteFilter !== 'Todos' && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-bold bg-purple-50 text-purple-700">
                Cliente: {clientesList.find(c => String(c.id) === clienteFilter)?.nome || clienteFilter}
                <button type="button" onClick={() => setClienteFilter('Todos')} className="hover:text-red-600">
                  <X size={11} />
                </button>
              </span>
            )}

            {(dataInicio || dataFim) && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-bold bg-amber-50 text-amber-700">
                Data: {dataInicio ? formatDisplayDate(dataInicio) : 'início'} até {dataFim ? formatDisplayDate(dataFim) : 'hoje'}
                <button type="button" onClick={() => { setDataInicio(''); setDataFim(''); }} className="hover:text-red-600">
                  <X size={11} />
                </button>
              </span>
            )}

            {(minVal || maxVal) && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-bold bg-emerald-50 text-emerald-700">
                Faixa: {minVal ? `≥ R$ ${minVal}` : ''} {maxVal ? `≤ R$ ${maxVal}` : ''}
                <button type="button" onClick={() => { setMinVal(''); setMaxVal(''); }} className="hover:text-red-600">
                  <X size={11} />
                </button>
              </span>
            )}

            {sortBy !== 'recentes' && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-bold bg-slate-100 text-slate-700">
                Ordem: {sortBy}
                <button type="button" onClick={() => setSortBy('recentes')} className="hover:text-red-600">
                  <X size={11} />
                </button>
              </span>
            )}
          </div>
        )}

        {/* Barra de Resumo das Propostas Filtradas */}
        <div className="flex items-center justify-between gap-2 pt-2 text-xs font-medium text-slate-600 bg-slate-50 px-3 py-2 rounded-xl">
          <div>
            Exibindo <strong className="text-slate-900">{filteredPropostas.length}</strong> de <strong className="text-slate-900">{propostas.length}</strong> propostas
          </div>
          <button
            type="button"
            onClick={() => setMostrarFiltros(false)}
            className="text-slate-500 hover:text-slate-800 text-[11px] font-bold flex items-center gap-1 cursor-pointer transition-colors"
          >
            <ChevronUp size={13} /> Ocultar Filtros
          </button>
        </div>

      </div>
      )}

      {/* Visualização em Lista / Cards para Mobile (< md) e Tabela para Desktop (>= md) */}
      <div className="bg-white rounded-xl shadow-xs overflow-hidden border border-slate-200">
        
        {/* Tabela Desktop */}
        <div className="hidden md:block overflow-x-auto max-w-full">
          <table className="w-full text-left min-w-[760px]">
            <thead>
              <tr className="bg-slate-50 border-b text-xs font-bold uppercase tracking-wider text-slate-600">
                <th className="w-10 p-3 text-center">
                  <span className="sr-only">Expandir</span>
                </th>
                <th className="p-4">Nº / Data</th>
                <th className="p-4">Cliente</th>
                <th className="p-4">Serviços Contratados</th>
                <th className="p-4">Status</th>
                <th className="p-4 text-center">
                  <span title="Controle de cotas e saldo de fretes interno do sistema">Saldo (Sistema)</span>
                </th>
                <th className="p-4">Total</th>
                <th className="p-4 text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-sm">
            {sortedPropostas.length === 0 ? (
              <tr>
                <td colSpan={8} className="p-10 text-center text-slate-400 font-medium space-y-2">
                  <p>{propostas.length === 0 ? 'Nenhuma proposta cadastrada no sistema.' : 'Nenhuma proposta corresponde aos filtros aplicados.'}</p>
                  {activeFiltersCount > 0 && (
                    <button
                      type="button"
                      onClick={handleClearAllFilters}
                      className="text-xs font-bold text-red-600 hover:text-red-700 underline cursor-pointer"
                    >
                      Limpar todos os filtros para ver tudo
                    </button>
                  )}
                </td>
              </tr>
            ) : (
              paginatedPropostas.map(p => {
                const isExpanded = expandedIds.includes(p.id);
                const total = calculateTotal(p);
                const dataDisplay = formatDisplayDate(p.data_abertura || p.created_at);
                const numProposta = p.numero_proposta || p.id;
                const saldoInfo = saldosPorProposta.get(p.id);

                return (
                  <React.Fragment key={p.id}>
                    <tr className={`border-b hover:bg-slate-50 transition-colors ${isExpanded ? 'bg-slate-50/80' : ''}`}>
                      <td className="p-3 text-center">
                        <button
                          type="button"
                          onClick={() => toggleExpand(p.id)}
                          className={`p-1.5 rounded-lg text-slate-500 hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer ${
                            isExpanded ? 'bg-red-50 text-red-600' : ''
                          }`}
                          title={isExpanded ? 'Recolher detalhes' : 'Expandir detalhes'}
                        >
                          {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                        </button>
                      </td>
                      <td 
                        className="p-4 cursor-pointer"
                        onClick={() => toggleExpand(p.id)}
                      >
                        <div className="flex items-center gap-1.5">
                          <span className="font-mono font-bold text-xs bg-slate-100 text-slate-800 px-2 py-0.5 rounded-md">
                            #{numProposta}
                          </span>
                        </div>
                        <div className="text-[11px] text-slate-400 font-medium mt-0.5 flex items-center gap-1">
                          <Calendar size={11} /> {dataDisplay}
                        </div>
                      </td>
                      <td 
                        className="p-4 font-semibold text-slate-900 cursor-pointer"
                        onClick={() => toggleExpand(p.id)}
                      >
                        {p.pessoa?.nome || 'Cliente não informado'}
                      </td>
                      <td 
                        className="p-4 max-w-xs truncate text-slate-600 cursor-pointer" 
                        title={p.detalhes_do_pedido?.map((d: any) => fixEncodingIssues(d.servicos)).filter(Boolean).join(', ')}
                        onClick={() => toggleExpand(p.id)}
                      >
                        {p.detalhes_do_pedido?.map((d: any) => fixEncodingIssues(d.servicos)).filter(Boolean).join(', ') || 'Nenhum serviço'}
                      </td>
                      <td className="p-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                          (p.status || '').toLowerCase() === 'aprovado' ? 'bg-green-100 text-green-800' :
                          (p.status || '').toLowerCase() === 'enviado' ? 'bg-blue-100 text-blue-800' :
                          (p.status || '').toLowerCase() === 'reprovado' ? 'bg-red-100 text-red-800' :
                          'bg-amber-100 text-amber-800'
                        }`}>
                          {p.status || 'Rascunho'}
                        </span>
                      </td>
                      <td className="p-4 text-center cursor-pointer" onClick={() => toggleExpand(p.id)}>
                        {(() => {
                          if (!saldoInfo || saldoInfo.totalContratado === 0) {
                            return (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium text-slate-500 bg-slate-100">
                                Sob Demanda
                              </span>
                            );
                          }
                          switch (saldoInfo.nivelAlertaGeral) {
                            case 'esgotado_0':
                              return (
                                <div className="inline-flex flex-col items-center gap-0.5">
                                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-800 border border-red-200">
                                    <ShieldAlert size={12} className="text-red-600" />
                                    0/{saldoInfo.totalContratado}
                                  </span>
                                  <span className="text-[10px] text-red-600 font-bold uppercase">⛔ Bloqueado</span>
                                </div>
                              );
                            case 'urgente_1':
                              return (
                                <div className="inline-flex flex-col items-center gap-0.5">
                                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-red-50 text-red-700 border border-red-300 animate-pulse">
                                    <AlertTriangle size={12} className="text-red-600" />
                                    1/{saldoInfo.totalContratado} un
                                  </span>
                                  <span className="text-[10px] text-red-700 font-semibold">Última Cota</span>
                                </div>
                              );
                            case 'critico_5':
                              return (
                                <div className="inline-flex flex-col items-center gap-0.5">
                                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-orange-100 text-orange-800 border border-orange-200">
                                    <AlertCircle size={12} className="text-orange-600" />
                                    {saldoInfo.saldoRestante}/{saldoInfo.totalContratado} un
                                  </span>
                                  <span className="text-[10px] text-orange-700 font-semibold">Crítico</span>
                                </div>
                              );
                            case 'baixo_10':
                              return (
                                <div className="inline-flex flex-col items-center gap-0.5">
                                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200">
                                    <AlertCircle size={12} className="text-amber-600" />
                                    {saldoInfo.saldoRestante}/{saldoInfo.totalContratado} un
                                  </span>
                                  <span className="text-[10px] text-amber-800 font-medium">Saldo Baixo</span>
                                </div>
                              );
                            case 'normal':
                            default:
                              return (
                                <div className="inline-flex flex-col items-center gap-0.5">
                                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800 border border-emerald-200">
                                    <CheckCircle2 size={12} className="text-emerald-600" />
                                    {saldoInfo.saldoRestante}/{saldoInfo.totalContratado} disp.
                                  </span>
                                  <span className="text-[10px] text-slate-400 font-mono">{saldoInfo.totalUtilizado} emitidas</span>
                                </div>
                              );
                          }
                        })()}
                      </td>
                      <td className="p-4 font-bold text-slate-900">
                        {total.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                      </td>
                      <td className="p-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button 
                            type="button"
                            onClick={() => toggleExpand(p.id)}
                            className="text-xs text-slate-600 hover:text-red-600 bg-slate-100 hover:bg-red-50 px-2.5 py-1.5 rounded-md font-medium transition-colors cursor-pointer"
                          >
                            {isExpanded ? 'Ocultar' : 'Detalhes'}
                          </button>
                          <button 
                            type="button"
                            onClick={() => handleDownloadPdf(p)} 
                            disabled={generatingPdfId === p.id}
                            className="text-red-600 hover:text-red-800 transition-colors bg-red-50 hover:bg-red-100 p-2 rounded-md cursor-pointer disabled:opacity-50"
                            title="Gerar PDF Comercial"
                          >
                            <FileText size={16} className={generatingPdfId === p.id ? 'animate-pulse' : ''} />
                          </button>
                          <ProtectedField permission="manage_proposals" hideMode="remove">
                            <button 
                              type="button"
                              onClick={() => handleEdit(p.id)} 
                              className="text-red-600 hover:text-red-800 transition-colors bg-red-50 hover:bg-red-100 p-2 rounded-md cursor-pointer"
                              title="Editar proposta"
                            >
                              <Edit2 size={16} />
                            </button>
                          </ProtectedField>
                        </div>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td colSpan={8} className="p-0 border-b">
                          <PropostaDetailAccordion proposta={p} total={total} saldoInfo={saldoInfo} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
        </div>

        {/* Visualização Mobile em Cards (< md) */}
        <div className="block md:hidden divide-y divide-slate-100">
          {sortedPropostas.length === 0 ? (
            <div className="p-8 text-center text-slate-400 font-medium space-y-2">
              <p>{propostas.length === 0 ? 'Nenhuma proposta cadastrada no sistema.' : 'Nenhuma proposta corresponde aos filtros aplicados.'}</p>
              {activeFiltersCount > 0 && (
                <button
                  type="button"
                  onClick={handleClearAllFilters}
                  className="text-xs font-bold text-red-600 hover:text-red-700 underline cursor-pointer"
                >
                  Limpar todos os filtros
                </button>
              )}
            </div>
          ) : (
            paginatedPropostas.map(p => {
              const isExpanded = expandedIds.includes(p.id);
              const total = calculateTotal(p);
              const dataDisplay = formatDisplayDate(p.data_abertura || p.created_at);
              const numProposta = p.numero_proposta || p.id;
              const saldoInfo = saldosPorProposta.get(p.id);
              const servicosTexto = p.detalhes_do_pedido?.map((d: any) => fixEncodingIssues(d.servicos)).filter(Boolean).join(', ') || 'Nenhum serviço especificado';

              return (
                <div key={p.id} className="p-3.5 space-y-3 hover:bg-slate-50/60 transition-colors">
                  {/* Topo do Card Mobile */}
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="font-mono font-bold text-xs bg-slate-100 text-slate-800 px-2 py-0.5 rounded-md">
                          #{numProposta}
                        </span>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${
                          (p.status || '').toLowerCase() === 'aprovado' ? 'bg-green-100 text-green-800' :
                          (p.status || '').toLowerCase() === 'enviado' ? 'bg-blue-100 text-blue-800' :
                          (p.status || '').toLowerCase() === 'reprovado' ? 'bg-red-100 text-red-800' :
                          'bg-amber-100 text-amber-800'
                        }`}>
                          {p.status || 'Rascunho'}
                        </span>
                      </div>
                      <div className="text-[11px] text-slate-400 font-medium mt-1 flex items-center gap-1">
                        <Calendar size={11} /> {dataDisplay}
                      </div>
                    </div>

                    {/* Saldo Pill Mobile */}
                    <div className="text-right">
                      {(() => {
                        if (!saldoInfo || saldoInfo.totalContratado === 0) {
                          return (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium text-slate-500 bg-slate-100">
                              Sob Demanda
                            </span>
                          );
                        }
                        switch (saldoInfo.nivelAlertaGeral) {
                          case 'esgotado_0':
                            return (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-red-800 border border-red-200">
                                <ShieldAlert size={11} className="text-red-600" />
                                0/{saldoInfo.totalContratado} cotas
                              </span>
                            );
                          case 'urgente_1':
                            return (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-50 text-red-700 border border-red-300 animate-pulse">
                                <AlertTriangle size={11} className="text-red-600" />
                                1/{saldoInfo.totalContratado} un
                              </span>
                            );
                          case 'critico_5':
                            return (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-orange-100 text-orange-800 border border-orange-200">
                                <AlertCircle size={11} className="text-orange-600" />
                                {saldoInfo.saldoRestante}/{saldoInfo.totalContratado} un
                              </span>
                            );
                          case 'baixo_10':
                            return (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800 border border-amber-200">
                                <AlertCircle size={11} className="text-amber-600" />
                                {saldoInfo.saldoRestante}/{saldoInfo.totalContratado} un
                              </span>
                            );
                          case 'normal':
                          default:
                            return (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-100 text-emerald-800 border border-emerald-200">
                                <CheckCircle2 size={11} className="text-emerald-600" />
                                {saldoInfo.saldoRestante}/{saldoInfo.totalContratado} disp.
                              </span>
                            );
                        }
                      })()}
                    </div>
                  </div>

                  {/* Nome do Cliente e Serviços */}
                  <div>
                    <h4 className="text-xs font-bold text-slate-900 leading-snug">
                      {p.pessoa?.nome || 'Cliente não informado'}
                    </h4>
                    <p className="text-[11px] text-slate-500 line-clamp-2 mt-0.5 leading-tight">
                      {servicosTexto}
                    </p>
                  </div>

                  {/* Total e Ações */}
                  <div className="flex items-center justify-between pt-2 border-t border-slate-100 gap-2">
                    <div>
                      <span className="text-[10px] text-slate-400 font-medium block">Valor Total</span>
                      <span className="text-sm font-extrabold text-slate-900">
                        {total.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                      </span>
                    </div>

                    <div className="flex items-center gap-1.5">
                      <button 
                        type="button"
                        onClick={() => toggleExpand(p.id)}
                        className={`text-xs px-2.5 py-1.5 rounded-lg font-bold transition-colors cursor-pointer ${
                          isExpanded ? 'bg-red-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                        }`}
                      >
                        {isExpanded ? 'Ocultar' : 'Detalhes'}
                      </button>

                      <button 
                        type="button"
                        onClick={() => handleDownloadPdf(p)} 
                        disabled={generatingPdfId === p.id}
                        className="text-red-600 hover:text-red-800 transition-colors bg-red-50 hover:bg-red-100 p-2 rounded-lg cursor-pointer disabled:opacity-50"
                        title="Gerar PDF Comercial"
                      >
                        <FileText size={15} className={generatingPdfId === p.id ? 'animate-pulse' : ''} />
                      </button>

                      <ProtectedField permission="manage_proposals" hideMode="remove">
                        <button 
                          type="button"
                          onClick={() => handleEdit(p.id)} 
                          className="text-red-600 hover:text-red-800 transition-colors bg-red-50 hover:bg-red-100 p-2 rounded-lg cursor-pointer"
                          title="Editar proposta"
                        >
                          <Edit2 size={15} />
                        </button>
                      </ProtectedField>
                    </div>
                  </div>

                  {/* Accordion Expandido no Card Mobile */}
                  {isExpanded && (
                    <div className="pt-2">
                      <PropostaDetailAccordion proposta={p} total={total} saldoInfo={saldoInfo} />
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        <Pagination
          currentPage={currentPage}
          totalItems={sortedPropostas.length}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={(items) => {
            setItemsPerPage(items);
            setCurrentPage(1);
          }}
        />
      </div>

      {isFormOpen && (
        <PropostaForm 
          pedidoId={selectedPropostaId} 
          onClose={handleCloseForm} 
          onSave={handleCloseForm} 
        />
      )}
    </div>
  );
}

